'use client'

import type React from 'react'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

// Tipos para o componente AnimatedCounter
interface AnimatedCounterProps {
  end: number
  duration?: number
  suffix?: string
  prefix?: string
  className?: string
  start?: number
}

// Hook customizado para animação de números
const useCountUp = (
  end: number,
  duration = 2000,
  start = 0
): [number, () => void] => {
  const [count, setCount] = useState<number>(start)
  const [isAnimating, setIsAnimating] = useState<boolean>(false)

  const animate = (): void => {
    if (isAnimating) return

    setIsAnimating(true)
    const startTime: number = Date.now()
    const startValue: number = start
    const endValue: number = end

    const updateCount = (): void => {
      const now: number = Date.now()
      const progress: number = Math.min((now - startTime) / duration, 1)
      const easeOut: number = 1 - (1 - progress) ** 3
      const currentValue: number = Math.floor(
        startValue + (endValue - startValue) * easeOut
      )

      setCount(currentValue)

      if (progress < 1) {
        requestAnimationFrame(updateCount)
      } else {
        setIsAnimating(false)
      }
    }

    requestAnimationFrame(updateCount)
  }

  return [count, animate]
}

// Hook para detectar quando elemento entra na viewport
const useIntersectionObserver = (): [
  React.RefObject<HTMLDivElement | null>,
  boolean,
] => {
  const [hasIntersected, setHasIntersected] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const hasIntersectedRef = useRef<boolean>(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasIntersectedRef.current) {
          hasIntersectedRef.current = true
          setHasIntersected(true)
        }
      },
      { threshold: 0.3 }
    )

    const current = ref.current
    if (current) observer.observe(current)

    return () => {
      if (current) observer.unobserve(current)
      observer.disconnect()
    }
  }, [])

  return [ref, hasIntersected]
}

// Componente do contador animado com forwardRef
const AnimatedCounter = forwardRef(function AnimatedCounter(
  {
    end,
    duration = 2000,
    suffix = '',
    prefix = '',
    className = '',
    start = 0,
  }: AnimatedCounterProps,
  ref: React.Ref<{ triggerAnimation: () => void }>
) {
  const [count, animate] = useCountUp(end, duration, start)
  const animatedRef = useRef(false)

  useImperativeHandle(ref, () => ({
    triggerAnimation: () => {
      if (!animatedRef.current) {
        animatedRef.current = true
        animate()
      }
    },
  }))

  return (
    <span className={className}>
      {prefix}
      {count.toLocaleString('pt-BR')}
      {suffix}
    </span>
  )
})

// Componente que exibe os contadores
export function MetricDisplay() {
  const [ref, hasIntersected] = useIntersectionObserver()

  const counter1Ref = useRef<{ triggerAnimation: () => void }>(null)
  const counter2Ref = useRef<{ triggerAnimation: () => void }>(null)
  const counter3Ref = useRef<{ triggerAnimation: () => void }>(null)

  useEffect(() => {
    if (hasIntersected) {
      setTimeout(() => {
        counter1Ref.current?.triggerAnimation()
      }, 0)

      setTimeout(() => {
        counter2Ref.current?.triggerAnimation()
      }, 200)

      setTimeout(() => {
        counter3Ref.current?.triggerAnimation()
      }, 400)
    }
  }, [hasIntersected])

  return (
    <div className="mt-10 flex justify-between" ref={ref}>
      <div className="flex max-w-23.25 flex-col items-center gap-2 leading-none">
        <h3 className="font-bold text-3xl text-[#2E2E2E] md:text-4xl">
          <AnimatedCounter
            duration={2500}
            end={180}
            prefix="+"
            ref={counter1Ref}
          />
        </h3>
        <span className="font-normal text-[#656566] text-sm leading-4">
          Cursos Disponíveis
        </span>
      </div>

      <div className="flex max-w-34 flex-col items-center gap-2 leading-none">
        <h3 className="font-bold text-3xl text-[#2E2E2E] md:text-4xl">
          <AnimatedCounter
            duration={3000}
            end={50}
            prefix="+"
            ref={counter2Ref}
            suffix=" mil"
          />
        </h3>
        <span className="font-normal text-[#656566] text-sm leading-4">
          Certificados Emitidos
        </span>
      </div>

      <div className="flex max-w-23.25 flex-col items-center gap-2 leading-none">
        <h3 className="font-bold text-3xl text-[#2E2E2E] md:text-4xl">
          <AnimatedCounter
            duration={2000}
            end={99}
            ref={counter3Ref}
            suffix="%"
          />
        </h3>
        <span className="font-normal text-[#656566] text-sm leading-4">
          Índice de Satisfação
        </span>
      </div>
    </div>
  )
}
